import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import { useParams, useSearchParams } from 'react-router-dom';
import DashboardGrid from 'features/dashboards/components/DashboardGrid';
import DashboardCard from 'features/dashboards/components/DashboardCard';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import { getPublicDashboard, runPublicCard } from 'features/dashboards/api/dashboardsApi';
import { errorText } from 'features/dashboards/format';

const WINDOWS = [['24h', 'Last 24 hours'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['90d', 'Last 90 days']];

/** /public/dashboards/:token — a shared dashboard, read-only, no sign-in. */
export default function PublicDashboardPage() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dash, setDash] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getPublicDashboard(token).then((d) => { setDash(d); document.title = d.name; }).catch((e) => setError(errorText(e)));
  }, [token]);

  const set = (key, value) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set(key, value); return n; }, { replace: true });
  const tabs = dash?.tabs || [];
  const tabId = tabs.some((t) => String(t.id) === searchParams.get('tab')) ? Number(searchParams.get('tab')) : tabs[0]?.id;
  const preset = WINDOWS.some(([k]) => k === searchParams.get('t')) ? searchParams.get('t') : (dash?.window?.preset || '7d');
  const run = useCallback((cardId) => runPublicCard(token, cardId, { preset }), [token, preset]);
  const cards = useMemo(() => (dash?.cards || []).filter((c) => c.tab_id === tabId), [dash, tabId]);
  const layout = useMemo(() => cards.map((c) => ({ i: String(c.id), x: c.x, y: c.y, w: c.w, h: c.h, static: true })), [cards]);

  if (error) {
    return <main className="flex min-h-screen items-center justify-center bg-surface p-8 text-[13px] text-[color:var(--text-muted)]" style={{ fontFamily: 'Inter, sans-serif' }}>This link is no longer available.</main>;
  }
  if (!dash) return <main className="flex min-h-screen items-center justify-center bg-surface"><TrodoLoader label="Loading" /></main>;

  return (
    <main className="min-h-screen bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="sticky top-0 z-20 border-b border-[color:var(--border-default)] bg-surface">
        <div className="flex min-h-[44px] flex-wrap items-center gap-3 px-4">
          <h1 className="text-[14px] font-semibold text-[color:var(--text-primary)]">{dash.name}</h1>
          <nav className="flex items-center gap-1" aria-label="Tabs">
            {tabs.length > 1 ? tabs.map((t) => (
              <button key={t.id} type="button" onClick={() => set('tab', String(t.id))} aria-current={t.id === tabId ? 'page' : undefined}
                className={`border-b-2 px-2.5 py-2.5 text-[13px] font-medium ${t.id === tabId ? 'border-[color:var(--brand-primary)] text-[color:var(--text-primary)]' : 'border-transparent text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'}`}>
                {t.name}
              </button>
            )) : null}
          </nav>
          <div className="ml-auto w-40">
            <BrandDropdown compact value={preset} ariaLabel="Window" onChange={(v) => set('t', v)}
              options={WINDOWS.map(([k, l]) => ({ value: k, label: l }))} />
          </div>
        </div>
      </div>
      <div className="px-3 pb-10 pt-3">
        <DashboardGrid key={tabId} layout={layout} editing={false} onLayoutChange={() => {}}>
          {cards.map((c) => <div key={String(c.id)}><DashboardCard card={c} run={run} runKey={preset} editing={false} /></div>)}
        </DashboardGrid>
      </div>
    </main>
  );
}
